// moneyEndpoints — the money routes Office calls, written down ONCE.
//
// CLAUDE.md: "Declare a contract once, and check it against the server."
// Hardcoding the same rules inside three screens is how one of them ends up
// offering a button the server answers 400 or 403 to. Everything here is read
// off the route files, and moneyEndpointsContract.test.js re-reads those files
// and fails if any of it drifts.
//
// ── The gate is the ROLE NAME, not the permission ───────────────────────────
//
// Every route below but one is guarded by `checkRole(...)`, which
// server/middleware/auth.js tests with `allowedRoles.includes(req.user.role)`
// and nothing else — there is no extra_permissions escape from it. The one,
// DELETE /timesheets/:id, does the same thing by hand inside its handler. So
// the UI has to gate on the ROLE too, and it has to do so for the SCREEN and
// not merely for the buttons on it.
//
// The permission model disagrees with those gates in BOTH directions, which is
// why guessing either way is wrong:
//
//   `useCan('invoices','update')` shows a Stripe button to an accounting user,
//   who holds the whole invoices permission set in database.js and is refused
//   by every WRITE route in invoices.js;
//
//   `useCan('invoices','read')` HIDES the invoice list from a manager, who
//   holds no invoices permission at all and whom every one of those same
//   routes serves.
//
// Those disagreements are recorded, with a reason each, in
// server/utils/__tests__/roleGateDivergence.test.js — most of them on
// invoices.js. No count is quoted here on purpose: the list shrinks whenever
// one is settled, and a number in prose goes quietly false (CLAUDE.md).
//
// One of them was settled on 2026-09-09 by moving a gate rather than a role
// list: GET /invoices/available-jobs — the finished-job picker, a READ, not in
// the table below — now asks the permission model, so an office user handed
// invoice rights through extra_permissions is served instead of 403'd. Every
// route in the table is still checkRole, which is why the table is still a
// list of role names.
//
// It was twenty-one and sixteen until 2026-09-05, when the four invoice READ
// routes were widened to admit accounting by owner decision — "bookkeeper
// should be able to see whatever he needs to keep the books". That is why the
// `roles` lists below are no longer uniform: reading the books and changing
// them are now different answers, and this table is where the difference is
// written down rather than inferred.
//
// ── Nothing here computes money ─────────────────────────────────────────────
//
// CLAUDE.md rule 4. The figures in a sync result are the server's, including
// the mismatch amounts, which arrive in CENTS (utils/stripeReconcile
// compareAmounts) and are shown as cents. Dividing them by 100 would be this
// app doing arithmetic on money, so it does not.

/**
 * name → { method, path, roles, file }
 *
 * `path` is the API path WITHOUT the /api prefix, the way apps/shared/http.js
 * takes it. `:id` marks the one substitution.
 */
export const MONEY_ENDPOINTS = {
  // ── the invoice screens themselves ────────────────────────────────────────
  //
  // Added 2026-09-05. These were the one part of Invoices still gated on
  // `useCan('invoices', …)` while every route behind them is `checkRole`, and
  // the effect was that nobody but an admin could invoice at all:
  //
  //   a MANAGER holds no `invoices` permission in database.js, so the screen
  //   refused them before it asked — while GET /api/invoices would have
  //   answered 200. Measured against a running server: the client rendered
  //   "You do not have access to invoices" with no request in the network log.
  //
  //   an ACCOUNTING user holds the WHOLE invoices permission set, so the
  //   screen let them in — and every route refused them 403. Measured: the
  //   list rendered "Could not load invoices", under two buttons that could
  //   only ever fail.
  //
  // The two roles were exactly swapped, which is what a second opinion about
  // permissions buys you. The doctrine in the header is not new; it had simply
  // been applied to the Stripe buttons and not to the screen around them.
  //
  // ACCOUNTING was added to the two READ entries on 2026-09-05, when the server
  // routes were widened to match — owner decision, and the reason the lists
  // below differ from one another. A bookkeeper can now open the list and read
  // an invoice; they still cannot create, edit, send, delete or mark one paid,
  // because none of that is seeing. server/utils/__tests__/bookkeeperAccess
  // .test.js holds both halves against the running app, and
  // moneyEndpointsContract.test.js re-derives every line here from the route
  // file, so this cannot drift from what the server actually does.
  invoiceList: { method: 'GET', path: '/invoices', roles: ['admin', 'manager', 'accounting'], file: 'invoices.js' },
  invoiceRead: { method: 'GET', path: '/invoices/:id', roles: ['admin', 'manager', 'accounting'], file: 'invoices.js' },
  invoiceCreate: { method: 'POST', path: '/invoices', roles: ['admin', 'manager'], file: 'invoices.js' },
  invoiceUpdate: { method: 'PUT', path: '/invoices/:id', roles: ['admin', 'manager'], file: 'invoices.js' },
  invoiceFromJobs: { method: 'POST', path: '/invoices/from-jobs', roles: ['admin', 'manager'], file: 'invoices.js' },
  invoiceSend: { method: 'POST', path: '/invoices/:id/send', roles: ['admin', 'manager'], file: 'invoices.js' },
  invoiceDelete: { method: 'DELETE', path: '/invoices/:id', roles: ['admin'], file: 'invoices.js' },
  // Added 2026-09-14. `invoices.archived` had been a column since v2.8.9 and
  // the Invoices screen had carried a "Show archived" toggle for as long as
  // it existed, while NOTHING could set the flag on an invoice — only on an
  // estimate, which shares the same table. Same role list as invoiceUpdate,
  // because archiving is an edit to the document's visibility: it takes the
  // invoice out of every default list, including the ones other people work
  // from. Not admin-only like invoiceDelete, because it is reversible from
  // the same screen by the same person.
  invoiceArchive: { method: 'POST', path: '/invoices/:id/archive', roles: ['admin', 'manager'], file: 'invoices.js' },
  invoiceUnarchive: { method: 'POST', path: '/invoices/:id/unarchive', roles: ['admin', 'manager'], file: 'invoices.js' },

  // ── Stripe, the sweep, and the two status flips ───────────────────────────
  stripeSettingsRead: { method: 'GET', path: '/invoices/stripe/settings', roles: ['admin'], file: 'invoices.js' },
  stripeSettingsSave: { method: 'POST', path: '/invoices/stripe/settings', roles: ['admin'], file: 'invoices.js' },
  syncOnePayment: { method: 'POST', path: '/invoices/:id/sync-payment', roles: ['admin', 'manager'], file: 'invoices.js' },
  syncAllPayments: { method: 'POST', path: '/invoices/sync-payments', roles: ['admin', 'manager'], file: 'invoices.js' },
  markInvoicePaid: { method: 'PUT', path: '/invoices/:id/mark-paid', roles: ['admin', 'manager', 'accounting'], file: 'invoices.js' },
  // Added 2026-09-05, by owner decision. It was in NO_SERVER_ROUTE below until
  // then, and that entry failing is how this got finished rather than
  // rediscovered — which is what the list is for.
  markInvoiceUnpaid: { method: 'PUT', path: '/invoices/:id/mark-unpaid', roles: ['admin', 'manager', 'accounting'], file: 'invoices.js' },
  // Added 2026-09-27, by owner decision: "We should be able to change the date
  // of paid, in case we forget to mark an invoice paid and then it ends up on
  // next year's report." Same roles as the pair above, because correcting the
  // day a payment was recorded is the same bookkeeping as recording it. It
  // moves no amount; it writes a `payment_date_changed` activity row.
  changeInvoicePaidDate: { method: 'PUT', path: '/invoices/:id/paid-date', roles: ['admin', 'manager', 'accounting'], file: 'invoices.js' },
  // Restored 2026-10-09 (classic Billing's "Record payment"). Part or all of
  // the balance; the server does the sum and the status. Same roles as the
  // three above.
  recordInvoicePayment: { method: 'POST', path: '/invoices/:id/payments', roles: ['admin', 'manager', 'accounting'], file: 'invoices.js' },
  // Added 2026-09-27, with the invoice email (docs/CLIENT_PORTAL.md §3). It was
  // in NO_SERVER_ROUTE below as `invoicePdfDownload` until then. The same file
  // the email attaches and the client downloads from their link, rendered by
  // server/utils/invoicePdf.js. Gated as reading an invoice is.
  invoicePdf: { method: 'GET', path: '/invoices/:id/pdf', roles: ['admin', 'manager', 'accounting'], file: 'invoices.js' },
  // timesheets.js does its own `req.user.role !== 'admin'` check INSIDE the
  // handler rather than using checkRole, so it will never show up in
  // roleGateDivergence.test.js. Same effect, and the same obligation here.
  // `perms` rather than `roles` since 2026-09-09. The handler asks
  // `userHasPermission(req, 'timesheets', 'delete')`, which server/database.js
  // seeds to admin ALONE — so who is refused has not changed. What changed is
  // that an admin can now GRANT it, and a `role !== 'admin'` comparison could
  // never see that grant.
  deleteTimesheet: { method: 'DELETE', path: '/timesheets/:id', perms: ['timesheets.delete'], file: 'timesheets.js' },
  // The one PDF the server actually renders, and the one entry whose mount is
  // not its filename — timeEntries.js is mounted at '/api/time-entries'
  // (server.js:250). `mount` is stated rather than derived because deriving it
  // gives '/timeEntries', which matches no route and would silently make the
  // contract check compare nothing.
  //
  // Its gate is an in-handler refusal (timeEntries.js:662), not checkRole:
  //
  //   req.user.role !== 'admin' && req.user.role !== 'manager'
  //     && req.user.id !== parseInt(userId)   → 403
  //
  // so ACCOUNTING is refused somebody else's paystub — while holding the full
  // `paystubs` baseline (database.js:1056) that opens the Payroll screen in the
  // first place. That is the same shape as the invoice divergence recorded
  // above, and it had the same result: a "Paystub PDF" button offered to a
  // bookkeeper on every row, which could only ever 403.
  //
  // The third clause is why `roles` is not the whole story: a worker may fetch
  // their OWN. Field asks that question by id, not through this table, so the
  // roles here describe the office case — somebody else's paystub.
  // `perms` rather than `roles` since 2026-09-09, and the gate moved out of
  // the handler into `requirePaystubPdf`. `paystubs.read` is seeded to admin,
  // manager and accounting — so the BOOKKEEPER can now fetch a paystub PDF,
  // which they could not before and which is plainly their job. The self
  // clause below is unchanged and is what lets Field hand a worker their own.
  paystubPdf: {
    method: 'GET', path: '/time-entries/paystub/:userId', mount: '/time-entries',
    perms: ['paystubs.read'], file: 'timeEntries.js', alsoSelf: true,
  },
};

/**
 * Capabilities the classic app has and the server has NO endpoint for.
 *
 * Recorded rather than built. A screen that offers one of these can only fail,
 * and adding the route is a change to money handling, which is a human's
 * (CLAUDE.md rule 4 and "Never modify money math without explicit
 * instruction"). moneyEndpointsContract.test.js asserts the server still has
 * no such route, so if one is ever added this list fails and the screen can be
 * finished instead of the gap being rediscovered.
 */
export const NO_SERVER_ROUTE = {
  // Empty since 2026-09-27: `invoicePdfDownload` became MONEY_ENDPOINTS.invoicePdf
  // when the server started rendering the invoice PDF for the email.
};

/**
 * What a reversal leaves behind, mirroring PUT /invoices/:id/mark-unpaid.
 *
 * Pure, so the screen can say what will happen BEFORE the request — a confirm
 * that guesses is worse than no confirm. The server is the authority and
 * applies the same rule; invoiceMarkUnpaid.test.js pins it there.
 *
 * Nothing is computed: `collected` is what the operator typed, echoed back.
 */
export function reversalOutcome(invoice, collectedInput) {
  const total = Number(invoice && invoice.total) || 0;
  const raw = collectedInput === undefined || collectedInput === null || collectedInput === ''
    ? 0
    : Number(collectedInput);
  if (!Number.isFinite(raw) || raw < 0) {
    return { problem: 'The amount collected must be a number, or left blank if nothing was.' };
  }
  if (total > 0 && raw >= total) {
    return { problem: 'That is the full amount, so it is paid in full. Use Mark paid instead.' };
  }
  return {
    problem: null,
    status: raw > 0 ? 'partial' : 'sent',
    collected: raw,
    // The server clears paid_at; the screen says so, because "it stays as
    // history" is the reasonable assumption and it is not what happens. The
    // record moves to the Activity log, which holds who and why as well.
    clearsPaidAt: true,
  };
}

/**
 * Where POST /invoices/:id/send would actually send this invoice — asked
 * BEFORE the button is offered, not after it is pressed.
 *
 * ── The dead control this exists for ────────────────────────────────────────
 *
 * Measured in Chromium at 320px on 2026-09-13, on a draft raised against a
 * client with no email: the confirm sheet read "This invoice has no email
 * address to go to. Add one to the client record first." and its SEND button
 * came back `disabled: false`. Pressing it spends a request to be told 400
 * `no_recipient` — a sentence the sheet had already printed, in red, above the
 * button. The server is right to refuse; the sheet knew the answer first and
 * offered the press anyway.
 *
 * ── It mirrors the server's own rule, per field ─────────────────────────────
 *
 * server/utils/invoiceContact.js resolves contact details from the CLIENT
 * record where there is one and falls back to the invoice's snapshot per
 * field; the route then refuses when the resolved email is empty. This is that
 * decision for the one field a send depends on, and moneyEndpointsContract
 * .test.js reads the route to check the refusal is still there.
 *
 * ── `known: false` is the whole reason this is not a one-line `!recipient` ──
 *
 * The screen asks GET /clients?active_only=true, so an INACTIVE client is not
 * in the roster it holds — while the server looks the client up by id whatever
 * its is_active says. A row the screen cannot see may therefore carry the
 * address the screen is about to claim does not exist. Disabling SEND on that
 * guess would be a FALSE REFUSAL on a money screen: an invoice nobody can send
 * from the app, for a reason the app made up.
 *
 * A client row is only evidence about its email if it actually carries the
 * field — `GET /clients` strips a client down to id and name for anyone
 * without `clients.read` (routes/clients.js PICKER_CLIENT_FIELDS), and a
 * missing key is not an empty address. So:
 *
 *   known: true    there is nowhere else an address could be hiding, so an
 *                  empty answer here is the same answer the server will give
 *   known: false   the client record was not readable; say so, and let the
 *                  server be the one to refuse
 *
 * Nothing about money is computed or read here.
 *
 * @param {object} invoice  the invoice row (client_id + client_email snapshot)
 * @param {object|null} client  the row from GET /clients, when the screen has it
 * @returns {{email: string|null, source: 'client'|'invoice'|'none',
 *            known: boolean, refusal: string|null}}
 */
export function sendRecipient(invoice, client) {
  const inv = invoice || {};
  const clean = (v) => {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    return s === '' ? null : s;
  };
  // Live first, snapshot second — resolveInvoiceContact's `pick`, for `email`.
  const live = clean(client && client.email);
  if (live) return { email: live, source: 'client', known: true, refusal: null };
  const snap = clean(inv.client_email);
  if (snap) return { email: snap, source: 'invoice', known: true, refusal: null };

  // Nothing we can see. Can anything we CANNOT see still hold an address?
  const carriesEmail = !!client && Object.prototype.hasOwnProperty.call(client, 'email');
  const known = !inv.client_id || carriesEmail;
  return {
    email: null,
    source: 'none',
    known,
    refusal: known
      ? 'This invoice has no email address to go to. Add one to the client record first.'
      : null,
  };
}

/** May this role use this endpoint? Unknown name or role → false, never true. */
export function mayUse(name, role) {
  const e = MONEY_ENDPOINTS[name];
  if (!e || !role) return false;
  // A row gated by CAPABILITY cannot be answered from a role, and guessing
  // would be worse than declining: the caller has the permissions on
  // `user.permissions` and should ask useCan, which is the same question the
  // server asks. Rows still gated by role answer as they always did.
  if (!e.roles) return false;
  return e.roles.includes(role);
}

/** The path with :id filled in. */
export function pathFor(name, id) {
  const e = MONEY_ENDPOINTS[name];
  if (!e) return null;
  return e.path.replace(':id', encodeURIComponent(id));
}

// ── Stripe key formats, as POST /invoices/stripe/settings enforces them ─────

export const PUBLISHABLE_PREFIX = 'pk_';
export const SECRET_PREFIXES = ['sk_', 'rk_'];

/**
 * The server's own three 400s, in the server's order, as a sentence — or null
 * when it would accept the pair.
 *
 * Checked here as well as there so a typo is named before the keys are sent
 * anywhere. The server is still the authority: whatever it answers is what
 * gets shown, this only saves a round trip on the obvious cases.
 */
export function rejectStripeKeys({ secret_key, publishable_key } = {}) {
  const secret = String(secret_key || '').trim();
  const publishable = String(publishable_key || '').trim();
  if (!secret || !publishable) return 'Both the secret key and the publishable key are required.';
  if (!publishable.startsWith(PUBLISHABLE_PREFIX)) {
    return 'Publishable key should start with pk_test_ or pk_live_';
  }
  if (!SECRET_PREFIXES.some((p) => secret.startsWith(p))) {
    return 'Secret key should start with sk_ (standard) or rk_ (restricted), in test or live mode';
  }
  return null;
}

/**
 * Do the keys and the live/test switch agree?
 *
 * The server does not check this — it stores whatever it is given — and
 * getting it wrong bills a real client through a test key or takes real money
 * through a page that says test. Advisory only: it never blocks a save,
 * because a restricted key (rk_) carries no mode in its name and a legitimate
 * pair can look mixed.
 */
export function liveModeWarning({ publishable_key, secret_key, is_live_mode } = {}) {
  const pk = String(publishable_key || '');
  const sk = String(secret_key || '');
  const keyed = [pk, sk].filter((k) => /^(pk|sk)_(test|live)_/.test(k));
  if (!keyed.length) return null;
  const live = keyed.filter((k) => k.includes('_live_')).length;
  const test = keyed.filter((k) => k.includes('_test_')).length;
  if (live && test) return 'One key is a live key and the other is a test key. Stripe will refuse the pair.';
  if (live && !is_live_mode) return 'These are LIVE keys but "Live mode" is off. Turn it on, or use test keys.';
  if (test && is_live_mode) return 'These are TEST keys but "Live mode" is on. No real payment can be taken with them.';
  return null;
}

/**
 * What GET /invoices/stripe/settings `state` means, for a person.
 *
 * The four values are the route's own (invoices.js, "v2.8.9: distinguish 'no
 * row' from 'row exists but secret won't decrypt'"). `undecryptable` is the
 * one that matters: it is not a fresh install, it is a server that can no
 * longer read the keys it stored, and it must not be reported as "not set up".
 */
export const STRIPE_STATES = {
  configured: { tone: 'ok', title: 'Stripe is connected', body: 'Invoices sent from here bill through Stripe, and the client can pay online.' },
  partial: { tone: 'warn', title: 'Half configured', body: 'A publishable key is on file but no secret key, so nothing can be charged. Enter both keys below.' },
  undecryptable: { tone: 'stop', title: 'The stored keys cannot be read', body: 'An encrypted secret key is on file but the server cannot read it — either the encryption key it was written with is not the one loaded now, or it was saved in a format this version no longer reads. Re-enter both keys below; that rewrites them in the current format with the current key.' },
  unconfigured: { tone: 'info', title: 'Stripe is not set up', body: 'Clients cannot pay online. Sent invoices go out as an emailed link instead.' },
};

/** The state, or null when the payload is not one this screen understands. */
export function stripeState(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const s = payload.state;
  return Object.prototype.hasOwnProperty.call(STRIPE_STATES, s) ? s : null;
}

// ── Reading a sync result ───────────────────────────────────────────────────

/**
 * POST /invoices/sync-payments answers with { checked, updated_count, updated,
 * amount_mismatches, failed, message }. This turns it into what the screen
 * renders, and it is pure so the negative cases can be pinned without a DOM.
 *
 * Counts: `checked` and `updated_count` are the SERVER's numbers and are used
 * as sent — never recounted from the arrays, which would let a truncated list
 * quietly change a figure. `failed` and `amount_mismatches` carry no count, so
 * their length is used; those are row counts, not money.
 *
 * Returns null for anything that is not an object, so a 200 carrying something
 * unexpected reads as "no answer" rather than as "nothing to do".
 */
export function syncSummary(result) {
  if (!result || typeof result !== 'object' || Array.isArray(result)) return null;
  const list = (v) => (Array.isArray(v) ? v : []);
  const num = (v) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : null);
  const updated = list(result.updated);
  const mismatches = list(result.amount_mismatches);
  const failed = list(result.failed);
  return {
    message: typeof result.message === 'string' && result.message.trim() ? result.message : null,
    checked: num(result.checked),
    updatedCount: num(result.updated_count),
    updated,
    mismatches,
    failed,
    // A sweep that could not reach some invoices, or that found Stripe holding
    // a different amount, is not a clean result even when others were updated.
    tone: failed.length || mismatches.length ? 'warn' : 'ok',
  };
}

/**
 * One amount mismatch as a sentence, in CENTS.
 *
 * compareAmounts (server/utils/stripeReconcile.js) sends invoiceCents,
 * stripeCents and deltaCents as integers. They are printed as integers: this
 * app does not convert money, and a wrong conversion here would look like a
 * plausible number, which CLAUDE.md calls the worst outcome available.
 */
export function mismatchSentence(m) {
  if (!m || typeof m !== 'object') return null;
  const has = (v) => v !== null && v !== undefined && Number.isFinite(Number(v));
  if (!has(m.invoiceCents) || !has(m.stripeCents)) return null;
  const who = m.invoice_number ? `${m.invoice_number}: ` : '';
  const delta = has(m.deltaCents) ? ` (difference ${m.deltaCents} cents)` : '';
  return `${who}the invoice says ${m.invoiceCents} cents, Stripe collected ${m.stripeCents} cents${delta}.`;
}
